import { motion } from 'framer-motion'
import { TrendingUp, DollarSign, Package, Layers } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'

const MarketFitROISection = () => {
  const roiData = [
    {
      icon: TrendingUp,
      title: 'Energy ROI',
      metric: '4–6 months',
      description: 'Payback by avoiding STEG tier jumps and optimizing peak demand.',
      color: 'from-yellow-500 to-orange-500',
    },
    {
      icon: DollarSign,
      title: 'Water ROI',
      metric: 'Immediate',
      description: 'Savings through real-time leak detection and automated tracking.',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      icon: Package,
      title: 'Retail ROI',
      metric: 'Significant',
      description: 'Direct margin protection through reduction in inventory shrinkage.',
      color: 'from-purple-500 to-pink-500',
    },
  ]

  return (
    <section id="roi" className="py-20 bg-background">
      <div className="section-container">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-foreground mb-6">
            Market Fit & <span className="gradient-text">ROI</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Rapid deployment and immediate financial impact with high return on investment
          </p>
        </motion.div>

        {/* ROI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {roiData.map((roi, index) => (
            <motion.div
              key={roi.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              whileHover={{ y: -10, scale: 1.02 }}
            >
              <Card className="h-full text-center hover:border-primary/30 transition-all duration-300">
                <CardContent className="p-8">
                  <div className={`w-20 h-20 bg-gradient-to-br ${roi.color} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                    <roi.icon className="text-white" size={40} />
                  </div>
                  <h3 className="text-2xl font-semibold text-foreground mb-2">
                    {roi.title}
                  </h3>
                  <div className="text-4xl font-display font-bold gradient-text mb-4">
                    {roi.metric}
                  </div>
                  <p className="text-muted-foreground leading-relaxed">
                    {roi.description}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Business Model */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Card>
            <CardContent className="p-8 md:p-12">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                <div>
                  <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-accent to-green-600 rounded-full px-4 py-2 mb-6">
                    <Layers className="text-white" size={18} />
                    <span className="text-white font-medium text-sm">Business Model</span>
                  </div>
                  <h3 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-6">
                    Hardware-as-a-Product with SaaS Optimization
                  </h3>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                    Our hybrid model combines reliable hardware deployment with continuous 
                    value delivery through our SaaS optimization layer, ensuring sustained 
                    ROI and long-term partnership with our clients.
                  </p>
                  <ul className="space-y-4">
                    {[
                      'One-time hardware investment with predictable costs',
                      'Subscription-based AI optimization and analytics',
                      'Continuous updates and feature enhancements',
                      'Scalable pricing based on deployment size',
                    ].map((item, i) => (
                      <motion.li
                        key={i}
                        className="flex items-start space-x-3 text-muted-foreground"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 * i }}
                      >
                        <div className="w-2 h-2 rounded-full bg-accent mt-2 flex-shrink-0" />
                        <span>{item}</span>
                      </motion.li>
                    ))}
                  </ul>
                </div>

                {/* Visual */}
                <div className="relative">
                  <motion.div
                    className="aspect-square bg-gradient-to-br from-primary/10 to-accent/10 rounded-2xl p-8 border"
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="h-full flex flex-col justify-center space-y-6">
                      {/* Hardware Layer */}
                      <motion.div
                        className="bg-secondary border rounded-xl p-6"
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 }}
                      >
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-primary to-primary/80 rounded-lg flex items-center justify-center">
                            <DollarSign className="text-white" size={24} />
                          </div>
                          <div>
                            <h4 className="text-foreground font-semibold">Hardware Layer</h4>
                            <p className="text-muted-foreground text-sm">Sensors, Nodes, Cameras</p>
                          </div>
                        </div>
                      </motion.div>

                      {/* Arrow */}
                      <motion.div
                        className="flex justify-center"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.4 }}
                      >
                        <div className="w-0.5 h-8 bg-gradient-to-b from-primary to-accent" />
                      </motion.div>

                      {/* SaaS Layer */}
                      <motion.div
                        className="bg-accent/10 border border-accent/30 rounded-xl p-6"
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.6 }}
                      >
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-accent to-green-600 rounded-lg flex items-center justify-center">
                            <TrendingUp className="text-white" size={24} />
                          </div>
                          <div>
                            <h4 className="text-foreground font-semibold">SaaS Optimization</h4>
                            <p className="text-muted-foreground text-sm">AI, Analytics, Dashboard</p>
                          </div>
                        </div>
                      </motion.div>

                      {/* Arrow */}
                      <motion.div
                        className="flex justify-center"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.8 }}
                      >
                        <div className="w-0.5 h-8 bg-gradient-to-b from-accent to-green-600" />
                      </motion.div>

                      {/* Value Layer */}
                      <motion.div
                        className="bg-green-50 border border-green-200 rounded-xl p-6"
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 1.0 }}
                      >
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-green-600 to-emerald-600 rounded-lg flex items-center justify-center">
                            <TrendingUp className="text-white" size={24} />
                          </div>
                          <div>
                            <h4 className="text-foreground font-semibold">Continuous Value</h4>
                            <p className="text-muted-foreground text-sm">ROI Growth & Optimization</p>
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  )
}

export default MarketFitROISection
